import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ASCIImaxLength, maxPageSize, maxStringLength } from '@app/global-constants';
import { ModalBase } from '@components/modal/modal';
import { ServerSideSearchSelectDataSource } from '@components/select/server-side-search-select.component';
import { TypOdbiorcy } from '@interfaces/komunikacja/enums/typy-odbiorcy.enum';
import { ZalacznikWiadomosciDto } from '@interfaces/komunikacja/komunikacja.interface';
import {
  UzytkownikIGrupaOdbiorcowDto,
  UzytkownikIGrupaOdbiorcowDtoQueryParams,
  WiadomoscDto,
} from '@interfaces/komunikacja/wersje-robocze.interface';
import { WiadomoscOdebranaDto } from '@interfaces/komunikacja/wiadomosc-odebrana.interface';
import { WiadomoscWyslanaDto } from '@interfaces/komunikacja/wiadomosc-wyslana.interface';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { WersjeRoboczeService } from '@services/api/komunikacja/wiadomosci-wersja-robocza.service';
import { saveAs } from 'file-saver';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  pipe,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';

import { WiadomosciFacade } from '../wiadomosci.facade';

@Component({
  templateUrl: './nowa-wiadomosc-modal.component.html',
})
export class NowaWiadomoscModalComponent extends ModalBase implements OnInit, OnDestroy {
  override recordData?: WiadomoscDto;
  recordId?: string;
  replyingTo?: Partial<WiadomoscOdebranaDto & WiadomoscWyslanaDto>;
  zalaczniki?: ZalacznikWiadomosciDto[];

  lastDraftEditTime?: Date;
  loaderControl = { sendMsgLoad: false, attachmentLoad: false, draftModeLoad: false };
  formChangesSub$?: Subscription;

  readonly maxTrescLength = maxStringLength;
  readonly maxTematLength = ASCIImaxLength;
  readonly wybraniOdbiorcy: { id: string; nazwa: string }[] = [];
  readonly odbiorcySelectControl = new FormControl(null, Validators.required);
  readonly odbiorcySource: ServerSideSearchSelectDataSource<
    WersjeRoboczeService,
    UzytkownikIGrupaOdbiorcowDto,
    UzytkownikIGrupaOdbiorcowDtoQueryParams
  > = {
    dataService: this.wiadomosciFacade.wersjeRoboczeService,
    getRecords: this.wiadomosciFacade.wersjeRoboczeService.getUzytkownicyIGrupyOdbiorcow,
    searchField: 'nazwa',
    recordsOperations: pipe(
      map((res) => ({
        ...res,
        data: res.data
          .filter((el) => (el.liczbaOdbiorcowWGrupie !== undefined ? el.liczbaOdbiorcowWGrupie > 0 : el))
          .map((el) => ({
            ...el,
            symbol: !isNaN(el.liczbaOdbiorcowWGrupie) ? `${$localize`odbiorców:`} ${el.liczbaOdbiorcowWGrupie}` : null,
          })),
      })),
    ),
  };

  constructor(
    override readonly activeModal: NgbActiveModal,
    protected override readonly fb: FormBuilder,
    readonly wiadomosciFacade: WiadomosciFacade,
  ) {
    super(activeModal);
    this.closeBtnLabel = $localize`Wyślij`;
    this.form = fb.group({
      odbiorcy: [[], [Validators.required]],
      temat: [null, [Validators.required, Validators.maxLength(this.maxTematLength)]],
      tresc: [null, [Validators.maxLength(this.maxTrescLength)]],
    });
  }

  private get odbiorcyFC(): FormControl {
    return this.form.get('odbiorcy') as FormControl;
  }

  ngOnInit(): void {
    if (this.recordData) {
      this.recordId = this.recordData.id;
      this.getAttachments();
      const odbiorcyForSelect = [
        ...new Set(
          this.recordData.odbiorcy.map((o) => ({
            id: o.odbiorcaId,
            nazwa: o.odbiorcaNazwa,
            typOdbiorcy: TypOdbiorcy.Uzytkownik,
            symbol: null,
            disabled: false,
          })),
        ),
      ];
      this.odbiorcySelectControl.setValue(odbiorcyForSelect);
      const odbiorcy = odbiorcyForSelect.map((o) => o.id);
      this.form.patchValue({
        ...this.recordData,
        odbiorcy,
      });
    } else if (this.replyingTo) {
      const odbiorcyIds = this.replyingTo.odbiorcy.map((el) => el.odbiorcaId);
      const odbiorcy = this.replyingTo.odbiorcy.map((el) => ({
        odbiorcaId: el.odbiorcaId,
        nazwa: el.odbiorcaNazwa,
      }));

      this.odbiorcySelectControl.setValue(odbiorcy);
      this.form.patchValue({
        odbiorcy: odbiorcyIds,
        temat: this.replyingTo.temat,
      });
    }
    this.title = this.recordData ? $localize`Edytowanie wiadomości` : $localize`Tworzenie wiadomości`;
    this.setFormChangesSub();
  }

  ngOnDestroy(): void {
    this.formChangesSub$.unsubscribe();
  }

  onAddOdbiorca(v: UzytkownikIGrupaOdbiorcowDto) {
    if (v) {
      if (v.typOdbiorcy === TypOdbiorcy.Uzytkownik) {
        this.form.controls.odbiorcy.setValue([...new Set([...this.form.controls.odbiorcy.value, v.id])]);
      } else if (v.typOdbiorcy === TypOdbiorcy.GrupaOdbiorcow) {
        this.wiadomosciFacade.grupyOdbiorcowService
          .getOdbiorcyGrupyOdbiorcow(v.id, { pageSize: maxPageSize })
          .subscribe(({ data }) => {
            const uzytkownicy = (this.odbiorcySelectControl.value as UzytkownikIGrupaOdbiorcowDto[]).filter(
              (el) => el.typOdbiorcy === TypOdbiorcy.Uzytkownik,
            );
            const mergedResults = [
              ...new Set(
                uzytkownicy.concat(
                  data.map((czlonek) => ({
                    id: czlonek.odbiorcaId,
                    nazwa: czlonek.nazwa,
                    typOdbiorcy: TypOdbiorcy.Uzytkownik,
                    email: czlonek.email,
                    symbol: null,
                    disabled: false,
                  })),
                ),
              ),
            ];

            this.odbiorcySelectControl.setValue(mergedResults);
            this.odbiorcyFC.setValue([...new Set(this.odbiorcyFC.value.concat(data.map((o) => o.odbiorcaId)))]);
          });
      }
    }
    this.odbiorcyFC.updateValueAndValidity();
  }

  onRemoveOdbiorca(v: UzytkownikIGrupaOdbiorcowDto): void {
    const updatedArr = this.odbiorcyFC.value;
    updatedArr.splice(
      this.odbiorcyFC.value.findIndex((o: string) => o === v.id),
      1,
    );
    this.odbiorcyFC.setValue(updatedArr);
    this.odbiorcyFC.updateValueAndValidity();
  }

  onOdbiorcyChange(v: string[]) {
    this.odbiorcyFC.markAsDirty();
    this.odbiorcyFC.markAsTouched();
    if (!v.length) {
      this.odbiorcyFC.setValue([]);
    }
  }

  onChangeContentValue(value: string): void {
    const contentControl = this.form.get('tresc');
    contentControl.setValue(value);
    contentControl.markAsDirty();
    contentControl.markAsTouched();
  }

  onUploadAttachmentFile(ev: Event): void {
    const input: Partial<HTMLInputElement> = ev.target;
    this.saveAttachment(Array.from(input.files));
  }

  downloadFile(file: ZalacznikWiadomosciDto): void {
    this.wiadomosciFacade.wersjeRoboczeService.downloadAttachment(this.recordId, file.id).subscribe((data) => {
      saveAs(new File([data], file.nazwaZalacznika));
    });
  }

  deleteAttachment(attachmentId: string): void {
    this.isSaving = true;
    this.wiadomosciFacade.wersjeRoboczeService
      .deleteAttachment(this.recordId, attachmentId)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe(() => {
        this.wiadomosciFacade.notificationService.showDeleted();
        this.getAttachments();
      });
  }

  handleSaveDraftBtnClick(): void {
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.isSaving = true;
    this.loaderControl.draftModeLoad = true;
    this.recordId ? this.updateDraft() : this.saveDraft();
  }

  submit = () => {
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.isSaving = true;
    this.loaderControl.sendMsgLoad = true;
    this.recordId ? this.editDraftAndSendMsg() : this.saveDraftAndSendMsg();
  };

  private getAttachments(): void {
    this.wiadomosciFacade.wersjeRoboczeService
      .getAttachments(this.recordId, { orderBy: 'nazwaZalacznika:asc', pageSize: maxPageSize })
      .subscribe(({ data }) => {
        this.zalaczniki = data;
      });
  }

  private saveAttachment(data: File[]): void {
    this.isSaving = true;
    this.loaderControl.attachmentLoad = true;
    forkJoin(this.setAttachmentRequest(data))
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.loaderControl.attachmentLoad = false;
        }),
      )
      .subscribe(() => {
        this.wiadomosciFacade.notificationService.showSaved();
        this.getAttachments();
      });
  }

  private setAttachmentRequest(data: File[]): Observable<null>[] {
    return data.map((file: File) => {
      const fd = new FormData();
      fd.append('nazwaZalacznika', file.name);
      fd.append('trescZalacznika', file.name);
      fd.append('file', file);
      return this.wiadomosciFacade.wersjeRoboczeService.setAttachment(this.recordId, fd);
    });
  }

  private saveDraft(): void {
    this.wiadomosciFacade.wersjeRoboczeService
      .saveDraft(this.form.value)
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.loaderControl.draftModeLoad = false;
        }),
      )
      .subscribe(({ id }) => {
        if (!this.recordData) {
          this.recordId = id;
        }
        this.lastDraftEditTime = new Date();
      });
  }

  private updateDraft(): void {
    this.wiadomosciFacade.wersjeRoboczeService
      .editDraft(this.recordId, this.form.value)
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.loaderControl.draftModeLoad = false;
        }),
      )
      .subscribe(() => {
        this.lastDraftEditTime = new Date();
      });
  }

  private editDraftAndSendMsg(): void {
    this.wiadomosciFacade.wersjeRoboczeService
      .editDraft(this.recordId, this.form.value)
      .pipe(
        switchMap(() => this.wiadomosciFacade.wersjeRoboczeService.sendMessage(this.recordId)),
        finalize(() => {
          this.isSaving = false;
          this.loaderControl.sendMsgLoad = false;
        }),
      )
      .subscribe(() => {
        this.wiadomosciFacade.notificationService.showSuccess({ message: $localize`Wiadomość wysłano` });
        this.close();
      });
  }

  private saveDraftAndSendMsg(): void {
    this.wiadomosciFacade.wersjeRoboczeService
      .saveDraft(this.form.value)
      .pipe(
        tap((res) => {
          if (res) {
            this.recordId = res.id;
          }
        }),
        switchMap(() => this.wiadomosciFacade.wersjeRoboczeService.sendMessage(this.recordId)),
        finalize(() => {
          this.isSaving = false;
          this.loaderControl.sendMsgLoad = false;
        }),
      )
      .subscribe(() => {
        this.wiadomosciFacade.notificationService.showSuccess({ message: $localize`Wiadomość wysłano` });
        this.close();
      });
  }

  private setFormChangesSub(): void {
    this.formChangesSub$ = this.form.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(3000),
        tap(() => {
          this.isSaving = true;
          this.loaderControl.draftModeLoad = true;
        }),
        mergeMap((value) => {
          const { odbiorcy, temat, tresc } = this.form.value;
          if (this.recordData || this.recordId) {
            return this.wiadomosciFacade.wersjeRoboczeService.editDraft(this.recordId, value);
          } else if ((odbiorcy?.length || temat?.length || tresc?.length) && this.form.dirty) {
            return this.wiadomosciFacade.wersjeRoboczeService.saveDraft(value);
          }
          return of(null);
        }),
        finalize(() => {
          this.isSaving = false;
          this.loaderControl.draftModeLoad = false;
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.recordId = res.id;
        }
        this.lastDraftEditTime = new Date();
        this.isSaving = false;
        this.loaderControl.draftModeLoad = false;
      });
  }
}
